import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export function useDailyStreamLimits() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data: streamLimit, isLoading } = useQuery({
    queryKey: ["dailyStreamLimit", user?.id],
    queryFn: async () => {
      if (!user) return null;

      console.log("Fetching daily stream limit for user:", user.id);

      // Call the function to ensure daily stream limit exists
      await supabase.rpc("get_or_create_daily_stream_limit", {
        p_user_id: user.id,
      });

      // Now fetch the actual daily stream limit data
      const { data, error } = await supabase
        .from("daily_stream_limits")
        .select("*")
        .eq("user_id", user.id)
        .eq("date", new Date().toISOString().split("T")[0])
        .single();

      if (error) {
        console.error("Error fetching daily stream limit:", error);
        return null;
      }

      console.log("Daily stream limit data:", data);
      return data;
    },
    enabled: !!user,
  });

  const checkHasRemainingStreams = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      const { data, error } = await supabase.rpc("has_remaining_streams", {
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error checking remaining streams:", error);
        return false;
      }

      return data === true;
    } catch (error) {
      console.error("Error checking remaining streams:", error);
      return false;
    }
  };

  const consumeStream = async (): Promise<boolean> => {
    if (!user) return false;

    try {
      console.log("Attempting to consume stream for user:", user.id);

      const { data, error } = await supabase.rpc("consume_stream", {
        p_user_id: user.id,
      });

      if (error) {
        console.error("Error consuming stream:", error);
        toast.error("Failed to process stream");
        return false;
      }

      const success = data === true;
      console.log("Stream consumption result:", success);

      if (success) {
        // Invalidate the query to refresh the UI
        queryClient.invalidateQueries({
          queryKey: ["dailyStreamLimit", user.id],
        });
      } else {
        toast.warning("Daily stream limit reached! (50 streams per day)");
      }

      return success;
    } catch (error) {
      console.error("Error consuming stream:", error);
      return false;
    }
  };

  return {
    streamLimit,
    isLoading,
    checkHasRemainingStreams,
    consumeStream,
    streamsUsed: streamLimit?.streams_used || 0,
    streamsRemaining: streamLimit?.streams_remaining || 50,
  };
}
